import type {FlagProps} from 'platform/components';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, type Nullish} from 'shared/web';

import type {StockTakeReport} from '../types/StockTakeReport';
import type {
  DailyStockTakeDetailView,
  DailyStockTakeItem,
  DailyStockTakeMoney,
} from '../utils/buildDailyStockTakeDetailView';
import {formatDailyStockTakeItem} from '../utils/formatDailyStockTakeItem';
import {useDailyStockTakeFormatters} from './useDailyStockTakeFormatters';

const POSITIVE_SIGN = 1;
const PERCENT_DECIMALS = 1;

export function useDailyStockTakeReport(view: DailyStockTakeDetailView, id: string | Nullish) {
  const {money, count} = useDailyStockTakeFormatters();
  const formatNumber = useFormatNumber();
  const dateTimeFormatter = useDateTimeFormatter();
  const day = (value: Date | null) =>
    isNotNil(value) ? dateTimeFormatter('dateShort', value) : EMPTY_PLACEHOLDER;
  const signed = (amount: DailyStockTakeMoney | null) =>
    isNotNil(amount) && Math.sign(amount.value) === POSITIVE_SIGN
      ? `+${money(amount)}`
      : money(amount);
  const differencePercent = isNotNil(view.differencePercent)
    ? `${formatNumber(view.differencePercent, PERCENT_DECIMALS) ?? EMPTY_PLACEHOLDER} %`
    : null;
  const cardsWithValue = (articles: number | null) =>
    `${i18n.t('entity.warehouse.reporting.cardsWithValue')}: ${count(articles)}`;
  const check = match<
    boolean | null,
    {colorScheme: FlagProps['colorScheme']; label: string} | null
  >(view.isCheckCorrect)
    .with(true, () => ({
      colorScheme: 'green' as const,
      label: i18n.t('entity.warehouse.reporting.checkMatches'),
    }))
    .with(false, () => ({
      colorScheme: 'red' as const,
      label: i18n.t('entity.warehouse.reporting.checkDoesNotMatch'),
    }))
    .otherwise(() => null);

  const metric = (id: string, value: string, note?: string | null) => ({
    id,
    label: i18n.t(`entity.warehouse.reporting.${id}`),
    value,
    note,
  });
  const breakdown = (
    items: DailyStockTakeItem[],
    totalId: string,
    total: DailyStockTakeMoney | null,
    articles?: number | null
  ) => [
    ...items.map((item) => ({
      id: item.id,
      ...formatDailyStockTakeItem(item),
      value: money(item.amount),
    })),
    metric(
      totalId,
      money(total),
      isNotNil(articles)
        ? i18n.t('entity.warehouse.reporting.articlesLine', {count: articles})
        : null
    ),
  ];

  const report: StockTakeReport = {
    kind: 'daily',
    filename: `daily-inventory-closing-${encodeURIComponent(id ?? day(view.closingDay))}.pdf`,
    title: i18n.t('page.warehouse.labels.dailyInventoryClosing'),
    metadata: [
      {
        id: 'warehouse',
        label: i18n.t('entity.warehouse.labels.warehouse'),
        value: view.warehouseLabel ?? EMPTY_PLACEHOLDER,
      },
      {
        id: 'branch',
        label: i18n.t('entity.warehouse.labels.branch'),
        value: view.branchLabel ?? EMPTY_PLACEHOLDER,
      },
      {id: 'day', label: i18n.t('general.labels.date'), value: day(view.closingDay)},
      {
        id: 'check',
        label: i18n.t('general.labels.status'),
        value: check?.label ?? EMPTY_PLACEHOLDER,
      },
      ...view.checkItems.map((item) => ({
        id: item.id,
        label: formatDailyStockTakeItem(item).label,
        value: money(item.amount),
      })),
      metric('checkTotal', money(view.checkSum)),
      ...(view.hasFailedArticles
        ? [metric('cardsFailingCheck', count(view.articlesCheckFailed))]
        : []),
    ],
    sections: [
      {
        id: 'completed',
        title: i18n.t('entity.warehouse.reporting.completedMovements'),
        groups: [
          {
            id: 'values',
            title: i18n.t('entity.warehouse.reporting.inventoryValues'),
            metrics: [
              metric(
                'openingValue',
                money(view.opening.amount),
                cardsWithValue(view.opening.articles)
              ),
              metric(
                'closingValue',
                money(view.closing.amount),
                cardsWithValue(view.closing.articles)
              ),
              metric('inventoryDifference', signed(view.difference), differencePercent),
            ],
          },
          {
            id: 'receipts',
            title: i18n.t('entity.warehouse.reporting.receiptsBreakdown'),
            metrics: breakdown(
              view.receiptItems,
              'dailyReceipts',
              view.receiptTotal,
              view.receiptArticles
            ),
          },
          {
            id: 'issues',
            title: i18n.t('entity.warehouse.reporting.issuesBreakdown'),
            metrics: breakdown(view.issueItems, 'dailyIssues', view.issueTotal, view.issueArticles),
          },
          {
            id: 'corrections',
            title: i18n.t('entity.warehouse.reporting.correctionsAndAdjustments'),
            metrics: [
              metric(
                'correctionsAndAdjustments',
                money(view.correctionTotal),
                isNotNil(view.correctionDocuments)
                  ? i18n.t('entity.warehouse.reporting.documentsLine', {
                      count: view.correctionDocuments,
                    })
                  : null
              ),
            ],
          },
        ],
      },
      {
        id: 'inProgress',
        title: i18n.t('entity.warehouse.reporting.inProgressMovements'),
        groups: [
          {
            id: 'values',
            title: i18n.t('general.labels.summary'),
            metrics: [
              metric('inProgressReceipts', money(view.inProgressReceiptTotal)),
              metric('inProgressIssues', money(view.inProgressIssueTotal)),
            ],
          },
          {
            id: 'receipts',
            title: i18n.t('entity.warehouse.reporting.receiptsBreakdown'),
            metrics: breakdown(
              view.inProgressReceiptItems,
              'inProgressReceipts',
              view.inProgressReceiptTotal
            ),
          },
          {
            id: 'issues',
            title: i18n.t('entity.warehouse.reporting.issuesBreakdown'),
            metrics: breakdown(
              view.inProgressIssueItems,
              'inProgressIssues',
              view.inProgressIssueTotal
            ),
          },
          {
            id: 'corrections',
            title: i18n.t('entity.warehouse.reporting.correctionsAndAdjustments'),
            metrics: [metric('inProgressCorrections', money(view.inProgressCorrectionTotal))],
          },
        ],
      },
    ],
    notes: [
      ...(isNotNil(view.openingSourceDay)
        ? [
            i18n.t('entity.warehouse.reporting.openingCarriedFrom', {
              date: day(view.openingSourceDay),
            }),
          ]
        : []),
      ...(view.hasDayGap ? [i18n.t('entity.warehouse.reporting.closingDayGapNote')] : []),
    ],
  };

  return {money, count, day, signed, differencePercent, cardsWithValue, check, report};
}
